import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Modal,
  ModalBody,
  ModalHeader,
  Progress,
  Row,
  Spinner,
} from "reactstrap";

import BreadCrumb from "../../Components/Common/BreadCrumb";
import HelpArticleLink from "../../Components/Common/HelpArticleLink";
import { HELP_ARTICLE_SUBSCRIPTION } from "../../helpers/helpCenter";
import {
  extractPaddleTransactionId,
  openPaddleCheckoutOverlay,
} from "../../helpers/paddleCheckout";
import { getAuthRoleNames } from "../../services/authService";
import { invalidateSessionCapabilities } from "../../services/sessionCapabilitiesService";
import PlanFeaturesCard from "../../Components/Studio/Plan/PlanFeaturesCard";
import {
  AvailablePlan,
  getMySubscription,
  getPendingPix,
  getPlans,
  MyTenantSubscription,
  SubscriptionCheckoutPix,
  syncSubscriptionPaymentStatus,
  TenantSubscriptionStatus,
} from "../../services/subscriptionService";
import CheckoutModal from "./Subscription/CheckoutModal";
import InvoicesSection from "./Subscription/InvoicesSection";
import PaymentMethodCard from "./Subscription/PaymentMethodCard";

const resolveIntlLocale = (language: string): string =>
  language === "sp" ? "es" : language;

const formatPeriodEnd = (iso: string, language: string): string => {
  const date = new Date(iso);

  return new Intl.DateTimeFormat(resolveIntlLocale(language), {
    dateStyle: "long",
  }).format(date);
};

const formatMoney = (
  cents: number,
  currency: string,
  language: string
): string =>
  new Intl.NumberFormat(resolveIntlLocale(language), {
    style: "currency",
    currency,
  }).format(cents / 100);

const calcUsagePercent = (
  current: number,
  max: number | null | undefined
): number | null => {
  if (max == null || max <= 0) {
    return null;
  }

  return Math.min(100, Math.round((current / max) * 100));
};

const progressColor = (percent: number | null): string => {
  if (percent == null) {
    return "success";
  }

  if (percent >= 100) {
    return "danger";
  }

  if (percent >= 80) {
    return "warning";
  }

  return "success";
};

const statusBadgeColor = (status: TenantSubscriptionStatus): string => {
  switch (status) {
    case "active":
      return "success";
    case "past_due":
      return "warning";
    case "suspended":
      return "danger";
    case "canceled":
      return "secondary";
    default:
      return "secondary";
  }
};

const POLL_INTERVAL_MS = 10_000;
const POLL_MAX_ATTEMPTS = 10;

const SubscriptionSettings = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isOwner = getAuthRoleNames().includes("owner");
  const paddleAutoOpenRef = useRef<string | null>(null);

  const [subscription, setSubscription] = useState<MyTenantSubscription | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [plansModalOpen, setPlansModalOpen] = useState(false);
  const [checkoutModalOpen, setCheckoutModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<AvailablePlan | null>(null);
  const [plans, setPlans] = useState<AvailablePlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [paymentPendingConfirmation, setPaymentPendingConfirmation] = useState(false);
  const [showPaymentPendingAlert, setShowPaymentPendingAlert] = useState(false);
  const [refreshingStatus, setRefreshingStatus] = useState(false);
  const [pendingPix, setPendingPix] = useState<SubscriptionCheckoutPix | null>(null);
  const [loadingPendingPix, setLoadingPendingPix] = useState(false);
  const [openingPaddle, setOpeningPaddle] = useState(false);

  useEffect(() => {
    document.title = t("settings.subscription.pageTitle");
  }, [t]);

  const refreshSubscriptionStatus = useCallback(
    async (options?: { showSuccessToast?: boolean }): Promise<boolean> => {
      try {
        const response = await syncSubscriptionPaymentStatus();
        setSubscription(response.data);

        if (response.data.status === "active") {
          setPaymentPendingConfirmation(false);
          setShowPaymentPendingAlert(false);
          invalidateSessionCapabilities();

          if (options?.showSuccessToast) {
            toast.success(
              response.message || t("settings.subscription.paymentPending.confirmed")
            );
          }

          return true;
        }

        if (options?.showSuccessToast) {
          toast.info(
            response.message || t("settings.subscription.checkout.payment.pixStillPending")
          );
        }

        return false;
      } catch (error: any) {
        toast.error(error?.data || t("settings.subscription.toast.loadError"));
        return false;
      }
    },
    [t]
  );

  const handleRefreshPaymentStatus = async (): Promise<void> => {
    setRefreshingStatus(true);

    try {
      await refreshSubscriptionStatus({ showSuccessToast: true });
    } finally {
      setRefreshingStatus(false);
    }
  };

  const handleDismissPaymentPendingAlert = (): void => {
    setShowPaymentPendingAlert(false);
    setPaymentPendingConfirmation(false);
  };

  useEffect(() => {
    if (!paymentPendingConfirmation) {
      return undefined;
    }

    let attempts = 0;
    let stopped = false;
    let intervalId = 0;

    const poll = async (): Promise<void> => {
      if (stopped) {
        return;
      }

      attempts += 1;
      const confirmed = await refreshSubscriptionStatus();

      if (confirmed || attempts >= POLL_MAX_ATTEMPTS) {
        stopped = true;

        if (intervalId) {
          window.clearInterval(intervalId);
        }

        if (confirmed) {
          toast.success(t("settings.subscription.paymentPending.confirmed"));
        }
      }
    };

    void poll();
    intervalId = window.setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      stopped = true;
      window.clearInterval(intervalId);
    };
  }, [paymentPendingConfirmation, refreshSubscriptionStatus, t]);

  const loadSubscription = useCallback(async () => {
    setLoading(true);

    try {
      const response = await getMySubscription();
      setSubscription(response.data);
      if (response.data.status === "active") {
        setPaymentPendingConfirmation(false);
        setShowPaymentPendingAlert(false);
      }
    } catch (error: any) {
      toast.error(error?.data || t("settings.subscription.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const loadPlans = useCallback(async () => {
    setPlansLoading(true);

    try {
      const response = await getPlans();
      setPlans(response.data);
    } catch (error: any) {
      toast.error(error?.data || t("settings.subscription.plans.toast.loadError"));
      setPlans([]);
    } finally {
      setPlansLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadSubscription();
  }, [loadSubscription]);

  useEffect(() => {
    if (plansModalOpen) {
      loadPlans();
    }
  }, [plansModalOpen, loadPlans]);

  const handleSelectPlan = (plan: AvailablePlan): void => {
    setSelectedPlan(plan);
    setPlansModalOpen(false);
    setCheckoutModalOpen(true);
  };

  const clearPtxnQuery = useCallback((): void => {
    if (!searchParams.has("_ptxn")) {
      return;
    }

    const next = new URLSearchParams(searchParams);
    next.delete("_ptxn");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const openPaddleCheckout = useCallback(
    async (transactionIdOrUrl: string, options?: { clearQuery?: boolean }) => {
      setOpeningPaddle(true);

      try {
        await openPaddleCheckoutOverlay(transactionIdOrUrl);
        setPaymentPendingConfirmation(true);
        setShowPaymentPendingAlert(true);
        toast.info(t("settings.subscription.checkout.paddle.opened"));

        if (options?.clearQuery) {
          clearPtxnQuery();
        }
      } catch (error: unknown) {
        const code = error instanceof Error ? error.message : "";

        if (code === "PADDLE_CLIENT_TOKEN_MISSING") {
          toast.error(t("settings.subscription.checkout.paddle.tokenMissing"));
        } else if (code === "PADDLE_TXN_MISSING") {
          toast.error(t("settings.subscription.checkout.paddle.txnMissing"));
        } else {
          toast.error(t("settings.subscription.checkout.paddle.openError"));
        }
      } finally {
        setOpeningPaddle(false);
      }
    },
    [clearPtxnQuery, t]
  );

  useEffect(() => {
    if (!isOwner || loading) {
      return;
    }

    const txnId = extractPaddleTransactionId(searchParams.get("_ptxn"));

    if (!txnId || paddleAutoOpenRef.current === txnId) {
      return;
    }

    paddleAutoOpenRef.current = txnId;
    void openPaddleCheckout(txnId, { clearQuery: true });
  }, [isOwner, loading, openPaddleCheckout, searchParams]);

  const handleCheckoutSuccess = (
    updated: MyTenantSubscription,
    message?: string,
    options?: { keepOpenForPix?: boolean; checkoutUrl?: string }
  ): void => {
    setSubscription(updated);
    setPaymentPendingConfirmation(
      updated.status === "suspended" || updated.status === "past_due"
    );
    setShowPaymentPendingAlert(
      updated.status === "suspended" || updated.status === "past_due"
    );
    invalidateSessionCapabilities();

    if (options?.keepOpenForPix) {
      toast.info(message || t("settings.subscription.checkout.payment.pixReady"));
      return;
    }

    setCheckoutModalOpen(false);
    setSelectedPlan(null);

    if (options?.checkoutUrl) {
      toast.info(message || t("settings.subscription.checkout.paddle.hint"));
      void openPaddleCheckout(options.checkoutUrl);
      return;
    }

    toast.success(message || t("settings.subscription.upgrade.toast.success"));
  };

  const handleOpenPlansModal = (): void => {
    setPlansModalOpen(true);
  };

  const canShowPendingPix =
    isOwner &&
    !!subscription &&
    subscription.payment_method === "pix" &&
    (subscription.status === "past_due" ||
      subscription.status === "suspended" ||
      subscription.status === "canceled");

  const pendingPaddleTxnId = useMemo(() => {
    if (
      !isOwner ||
      !subscription ||
      subscription.payment_method !== "paddle" ||
      !["past_due", "suspended", "canceled"].includes(subscription.status)
    ) {
      return null;
    }

    const externalId = subscription.external_subscription_id;

    return extractPaddleTransactionId(externalId);
  }, [isOwner, subscription]);

  const handleOpenPendingPix = async (): Promise<void> => {
    if (!subscription?.plan) {
      return;
    }

    setLoadingPendingPix(true);

    try {
      const response = await getPendingPix();
      setPendingPix(response.data);
      setSelectedPlan(subscription.plan);
      setCheckoutModalOpen(true);
      toast.info(response.message || t("settings.subscription.checkout.payment.pixReady"));
    } catch (error: any) {
      toast.error(error?.data || t("settings.subscription.checkout.payment.pixReloadError"));
    } finally {
      setLoadingPendingPix(false);
    }
  };

  const handlePixPaymentConfirmed = (updated: MyTenantSubscription): void => {
    setSubscription(updated);
    setPaymentPendingConfirmation(false);
    setShowPaymentPendingAlert(false);
    setCheckoutModalOpen(false);
    setSelectedPlan(null);
    setPendingPix(null);
    invalidateSessionCapabilities();
  };

  const professionalsPercent = useMemo(() => {
    if (!subscription?.plan) {
      return null;
    }

    return calcUsagePercent(
      subscription.current_professionals_count,
      subscription.plan.max_professionals
    );
  }, [subscription]);

  const unitsPercent = useMemo(() => {
    if (!subscription?.plan) {
      return null;
    }

    return calcUsagePercent(
      subscription.current_units_count,
      subscription.plan.max_units
    );
  }, [subscription]);

  const statusLabel = (status: TenantSubscriptionStatus): string => {
    // past_due + txn Paddle draft = aguardando checkout, não inadimplência.
    if (
      status === "past_due" &&
      (subscription?.gateway_slug === "paddle" ||
        subscription?.payment_method === "paddle") &&
      extractPaddleTransactionId(subscription?.external_subscription_id)
    ) {
      return t("settings.subscription.status.awaiting_payment");
    }

    return t(`settings.subscription.status.${status}`);
  };

  const professionalsUsageLabel = (): string => {
    if (!subscription?.plan) {
      return "";
    }

    const { current_professionals_count: current } = subscription;
    const max = subscription.plan.max_professionals;

    if (max == null || max <= 0) {
      return t("settings.subscription.usage.professionalsUnlimited", { current });
    }

    return t("settings.subscription.usage.professionals", { current, max });
  };

  const unitsUsageLabel = (): string => {
    if (!subscription?.plan) {
      return "";
    }

    const { current_units_count: current } = subscription;
    const max = subscription.plan.max_units;

    if (max == null || max <= 0) {
      return t("settings.subscription.usage.unitsUnlimited", { current });
    }

    return t("settings.subscription.usage.units", { current, max });
  };

  const planActionLabel = (plan: AvailablePlan): string => {
    if (subscription?.plan_id === plan.id) {
      return t("settings.subscription.plans.actions.renew");
    }

    return t("settings.subscription.plans.actions.upgrade");
  };

  const planLimitsLabel = (plan: AvailablePlan): string =>
    t("settings.subscription.plans.limits", {
      units: plan.max_units ?? "∞",
      professionals: plan.max_professionals ?? "∞",
    });

  return (
    <div className="page-content">
      <ToastContainer closeButton pauseOnHover />
      <Container fluid>
        <BreadCrumb
          title={t("settings.subscription.breadcrumb.title")}
          pageTitle={t("settings.subscription.breadcrumb.pageTitle")}
        />
        <div className="d-flex justify-content-end mb-3">
          <HelpArticleLink articlePath={HELP_ARTICLE_SUBSCRIPTION} />
        </div>
        <Row className="justify-content-center">
          <Col lg={10} xl={9}>
            <Card>
              <CardHeader className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                <h5 className="card-title mb-0">{t("settings.subscription.title")}</h5>
                {isOwner ? (
                  <div className="d-flex flex-wrap gap-2">
                    {canShowPendingPix ? (
                      <Button
                        color="success"
                        size="sm"
                        outline
                        disabled={loadingPendingPix}
                        onClick={() => void handleOpenPendingPix()}
                      >
                        {loadingPendingPix ? (
                          <Spinner size="sm" />
                        ) : (
                          t("settings.subscription.paymentPending.viewPix")
                        )}
                      </Button>
                    ) : null}
                    {pendingPaddleTxnId ? (
                      <Button
                        color="success"
                        size="sm"
                        outline
                        disabled={openingPaddle}
                        onClick={() => void openPaddleCheckout(pendingPaddleTxnId)}
                      >
                        {openingPaddle ? (
                          <Spinner size="sm" />
                        ) : (
                          t("settings.subscription.paymentPending.viewPaddle")
                        )}
                      </Button>
                    ) : null}
                    <Button color="primary" size="sm" onClick={handleOpenPlansModal}>
                      {t("settings.subscription.upgrade.openModal")}
                    </Button>
                  </div>
                ) : null}
              </CardHeader>
              <CardBody>
                {showPaymentPendingAlert ? (
                  <Alert
                    color="warning"
                    isOpen={showPaymentPendingAlert}
                    toggle={handleDismissPaymentPendingAlert}
                    className="d-flex flex-wrap align-items-center justify-content-between gap-2"
                  >
                    <div className="d-flex align-items-center gap-2">
                      <i className="ri-time-line fs-20" aria-hidden="true" />
                      <span>{t("settings.subscription.paymentPending.message")}</span>
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      {canShowPendingPix ? (
                        <Button
                          color="warning"
                          size="sm"
                          disabled={loadingPendingPix}
                          onClick={() => void handleOpenPendingPix()}
                        >
                          {loadingPendingPix ? (
                            <Spinner size="sm" />
                          ) : (
                            t("settings.subscription.paymentPending.viewPix")
                          )}
                        </Button>
                      ) : null}
                      {pendingPaddleTxnId ? (
                        <Button
                          color="warning"
                          size="sm"
                          disabled={openingPaddle}
                          onClick={() => void openPaddleCheckout(pendingPaddleTxnId)}
                        >
                          {openingPaddle ? (
                            <Spinner size="sm" />
                          ) : (
                            t("settings.subscription.paymentPending.viewPaddle")
                          )}
                        </Button>
                      ) : null}
                      <Button
                        color="warning"
                        size="sm"
                        outline
                        disabled={refreshingStatus}
                        onClick={handleRefreshPaymentStatus}
                      >
                        {refreshingStatus ? (
                          <Spinner size="sm" />
                        ) : (
                          t("settings.subscription.paymentPending.refresh")
                        )}
                      </Button>
                    </div>
                  </Alert>
                ) : null}
                {loading ? (
                  <div className="d-flex justify-content-center py-4">
                    <Spinner color="primary" size="sm" />
                  </div>
                ) : subscription?.plan ? (
                  <>
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4">
                      <div>
                        <p className="text-muted mb-1">
                          {t("settings.subscription.fields.plan")}
                        </p>
                        <h4 className="mb-0">{subscription.plan.name}</h4>
                      </div>
                      <Badge
                        color={statusBadgeColor(subscription.status)}
                        className="fs-12 text-uppercase"
                      >
                        {statusLabel(subscription.status)}
                      </Badge>
                    </div>

                    {isOwner ? (
                      <PaymentMethodCard
                        subscription={subscription}
                        onUpdated={setSubscription}
                      />
                    ) : null}

                    <Row className="g-3 mb-4">
                      <Col md={6}>
                        <p className="text-muted mb-1">
                          {t("settings.subscription.fields.basePrice")}
                        </p>
                        <p className="fw-medium mb-0">
                          {formatMoney(
                            subscription.plan.base_price,
                            subscription.plan.currency,
                            i18n.language
                          )}
                        </p>
                      </Col>
                      <Col md={6}>
                        <p className="text-muted mb-1">
                          {t("settings.subscription.fields.periodEnd")}
                        </p>
                        <p className="fw-medium mb-0">
                          {formatPeriodEnd(
                            subscription.current_period_end,
                            i18n.language
                          )}
                        </p>
                      </Col>
                    </Row>

                    <div className="mb-4">
                      <p className="text-muted mb-2">
                        {t("settings.subscription.usage.title")}
                      </p>

                      <div className="mb-3">
                        <div className="d-flex justify-content-between mb-1">
                          <span className="text-muted">
                            {t("settings.subscription.usage.professionalsLabel")}
                          </span>
                          <span>{professionalsUsageLabel()}</span>
                        </div>
                        {professionalsPercent != null ? (
                          <Progress
                            value={professionalsPercent}
                            color={progressColor(professionalsPercent)}
                          />
                        ) : null}
                      </div>

                      <div>
                        <div className="d-flex justify-content-between mb-1">
                          <span className="text-muted">
                            {t("settings.subscription.usage.unitsLabel")}
                          </span>
                          <span>{unitsUsageLabel()}</span>
                        </div>
                        {unitsPercent != null ? (
                          <Progress
                            value={unitsPercent}
                            color={progressColor(unitsPercent)}
                          />
                        ) : null}
                      </div>
                    </div>

                    {subscription.plan.description ? (
                      <p className="text-muted mb-0">{subscription.plan.description}</p>
                    ) : null}

                    <PlanFeaturesCard />
                  </>
                ) : (
                  <p className="text-muted mb-0">
                    {t("settings.subscription.empty")}
                  </p>
                )}
              </CardBody>
            </Card>

            {isOwner ? <InvoicesSection onSubscribe={handleOpenPlansModal} /> : null}
          </Col>
        </Row>
      </Container>

      <Modal
        isOpen={plansModalOpen}
        toggle={() => setPlansModalOpen(false)}
        centered
        size="lg"
      >
        <ModalHeader toggle={() => setPlansModalOpen(false)}>
          {t("settings.subscription.plans.title")}
        </ModalHeader>
        <ModalBody>
          {plansLoading ? (
            <div className="d-flex justify-content-center py-4">
              <Spinner color="primary" size="sm" />
            </div>
          ) : plans.length === 0 ? (
            <p className="text-muted mb-0">{t("settings.subscription.plans.empty")}</p>
          ) : (
            <Row className="g-3">
              {plans.map((plan) => {
                const isCurrentPlan = subscription?.plan_id === plan.id;

                return (
                  <Col md={6} key={plan.id}>
                    <Card className="border shadow-none h-100 mb-0">
                      <CardBody className="d-flex flex-column h-100">
                        <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
                          <h5 className="mb-0">{plan.name}</h5>
                          {isCurrentPlan ? (
                            <Badge color="success" className="fs-11">
                              {t("settings.subscription.plans.currentBadge")}
                            </Badge>
                          ) : null}
                        </div>
                        <p className="text-muted small mb-2">
                          {plan.description || t("settings.subscription.plans.noDescription")}
                        </p>
                        <p className="fw-semibold fs-5 mb-2">
                          {formatMoney(plan.base_price, plan.currency, i18n.language)}
                        </p>
                        <p className="text-muted small mb-3">{planLimitsLabel(plan)}</p>
                        <Button
                          color={isCurrentPlan ? "success" : "primary"}
                          outline={isCurrentPlan}
                          className="mt-auto"
                          onClick={() => handleSelectPlan(plan)}
                        >
                          {planActionLabel(plan)}
                        </Button>
                      </CardBody>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}
        </ModalBody>
      </Modal>

      <CheckoutModal
        isOpen={checkoutModalOpen}
        plan={selectedPlan}
        subscription={subscription}
        initialPix={pendingPix}
        onClose={() => {
          setCheckoutModalOpen(false);
          setSelectedPlan(null);
          setPendingPix(null);
        }}
        onSuccess={handleCheckoutSuccess}
        onPaymentConfirmed={handlePixPaymentConfirmed}
      />
    </div>
  );
};

export default SubscriptionSettings;
